/**
 * complianceUtils.js — Compliance code resolution for ENS 2024 / ECSBC 2024.
 *
 * Rules:
 *   Residential  → ENS 2024  (Energy and Norms for Sustainability — residential)
 *   Commercial   → ECSBC 2024 (Energy Conservation and Sustainability Building Code — commercial)
 *   Mixed-use    → whichever code the user selects; null when not yet chosen
 */

export const COMPLIANCE_LABELS = {
  ens_2024:   'ENS 2024',
  ecsbc_2024: 'ECSBC 2024',
};

/**
 * Returns the human-readable compliance code label for a building.
 * Returns null for mixed-use buildings where the user has not yet selected a code.
 *
 * @param {Object} building  — building object from the project store
 * @returns {string|null}
 */
export function getComplianceLabel(building) {
  const cat  = building?.building_category;
  const code = building?.compliance_code;
  if (cat === 'residential') return 'ENS 2024';
  if (cat === 'commercial')  return 'ECSBC 2024';
  if (cat === 'mixed')       return COMPLIANCE_LABELS[code] ?? null;
  // Fallback: use whatever is stored
  return COMPLIANCE_LABELS[code] ?? 'ECSBC 2024';
}

/**
 * Returns the compliance label with a safe non-null fallback.
 * Use when you need a string for display even if the user hasn't confirmed yet.
 */
export function getComplianceLabelSafe(building) {
  return getComplianceLabel(building) ?? 'ENS / ECSBC 2024';
}

/** True when the applicable code is ECSBC 2024. */
export function isEcsbc(building) {
  const cat  = building?.building_category;
  const code = building?.compliance_code;
  if (cat === 'commercial') return true;
  if (cat === 'mixed')      return code === 'ecsbc_2024';
  return false;
}

/** True when the applicable code is ENS 2024. */
export function isEns(building) {
  const cat  = building?.building_category;
  const code = building?.compliance_code;
  if (cat === 'residential') return true;
  if (cat === 'mixed')       return code === 'ens_2024';
  return false;
}

/**
 * Section-specific references for the Schedules screen.
 * Returns { scheduleRef, extLightRef, extEquipRef } per the applicable code.
 */
export function getComplianceScheduleRefs(building) {
  if (isEcsbc(building)) {
    return { scheduleRef: '§12.7', extLightRef: '§9.4', extEquipRef: '§9.6' };
  }
  if (isEns(building)) {
    return { scheduleRef: '§7', extLightRef: '§6', extEquipRef: '§6' };
  }
  // Mixed, not yet chosen
  return { scheduleRef: null, extLightRef: null, extEquipRef: null };
}
